import { applyPersistedFrameColors } from './configuratorColors';
import { isGlazing36mm } from './vmkGlazing';

/** Shared glazing choice across configurators (localStorage). */
export const GLAZING_STORAGE_KEY = 'config-go-glazing';

/** Shared PVC profile + wood joint across configurators (localStorage). */
export const SHARED_OPTIONS_STORAGE_KEY = 'config-go-shared-options';

const GLAZING_FIELDS = ['glazingType', 'rightGlazingType'];
const PVC_PROFILE_IDS = new Set(['schuco', 'synego']);
const WOOD_JOINT_IDS = new Set(['joint-45', 'joint-90']);

/** Terrasdeur uses double-24 / double-36; other pages use double / triple. */
const usesTerrasdeurGlazingIds = (sampleId) =>
  sampleId === 'double-24' || sampleId === 'double-36';

export function glazingThicknessFromId(glazingTypeId) {
  if (!glazingTypeId) return null;
  return isGlazing36mm(glazingTypeId) ? '36' : '24';
}

export function glazingIdForPage(thickness, sampleId) {
  const is36 = thickness === '36';
  if (usesTerrasdeurGlazingIds(sampleId)) {
    return is36 ? 'double-36' : 'double-24';
  }
  return is36 ? 'triple' : 'double';
}

export function loadPersistedGlazing() {
  try {
    const raw = localStorage.getItem(GLAZING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const result = {};
    if (parsed.glazingThickness === '24' || parsed.glazingThickness === '36') {
      result.glazingThickness = parsed.glazingThickness;
    }
    GLAZING_FIELDS.forEach((field) => {
      if (parsed[field]) result[field] = String(parsed[field]);
    });
    return Object.keys(result).length ? result : null;
  } catch {
    return null;
  }
}

export function savePersistedGlazing(updates) {
  if (!updates || typeof updates !== 'object') return;
  const current = loadPersistedGlazing() || {};
  const next = { ...current };
  if (updates.glazingThickness === '24' || updates.glazingThickness === '36') {
    next.glazingThickness = updates.glazingThickness;
  }
  GLAZING_FIELDS.forEach((field) => {
    if (updates[field]) next[field] = String(updates[field]);
  });
  localStorage.setItem(GLAZING_STORAGE_KEY, JSON.stringify(next));
}

export function applyPersistedGlazing(defaultConfig) {
  const saved = loadPersistedGlazing();
  if (!saved) return { ...defaultConfig };

  const thickness =
    saved.glazingThickness ||
    glazingThicknessFromId(saved.glazingType) ||
    glazingThicknessFromId(saved.rightGlazingType);

  if (!thickness) return { ...defaultConfig };

  const next = { ...defaultConfig };
  GLAZING_FIELDS.forEach((field) => {
    if (!(field in next)) return;
    next[field] = glazingIdForPage(thickness, next[field]);
  });
  return next;
}

export function trackGlazingChange(field, value) {
  if (!value || !GLAZING_FIELDS.includes(field)) return;
  const thickness = glazingThicknessFromId(value) || '24';
  savePersistedGlazing({
    glazingThickness: thickness,
    [field]: value,
  });
}

function loadPersistedSharedOptions() {
  try {
    const raw = localStorage.getItem(SHARED_OPTIONS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const result = {};
    if (PVC_PROFILE_IDS.has(parsed.profileSystem)) {
      result.profileSystem = parsed.profileSystem;
    }
    if (WOOD_JOINT_IDS.has(parsed.woodJoint)) {
      result.woodJoint = parsed.woodJoint;
    }
    return Object.keys(result).length ? result : null;
  } catch {
    return null;
  }
}

function savePersistedSharedOptions(updates) {
  if (!updates || typeof updates !== 'object') return;
  const current = loadPersistedSharedOptions() || {};
  const next = { ...current };
  if (PVC_PROFILE_IDS.has(updates.profileSystem)) {
    next.profileSystem = updates.profileSystem;
  }
  if (WOOD_JOINT_IDS.has(updates.woodJoint)) {
    next.woodJoint = updates.woodJoint;
  }
  localStorage.setItem(SHARED_OPTIONS_STORAGE_KEY, JSON.stringify(next));
}

export function applyPersistedSharedOptions(defaultConfig) {
  const saved = loadPersistedSharedOptions();
  if (!saved) return { ...defaultConfig };

  const next = { ...defaultConfig };
  // Only apply PVC profiles — never overwrite Keralit cladding profile IDs
  if (
    saved.profileSystem &&
    'profileSystem' in next &&
    PVC_PROFILE_IDS.has(next.profileSystem)
  ) {
    next.profileSystem = saved.profileSystem;
  }
  if (saved.woodJoint && 'woodJoint' in next) {
    next.woodJoint = saved.woodJoint;
  }
  return next;
}

/** Persist Schüco/Veka when the user changes profile on a PVC configurator. */
export function trackProfileSystemChange(field, value) {
  if (field !== 'profileSystem' || !value) return;
  if (!PVC_PROFILE_IDS.has(value)) return;
  savePersistedSharedOptions({ profileSystem: value });
}

/** Persist wood joint when the user changes it. */
export function trackWoodJointChange(field, value) {
  if (field !== 'woodJoint' || !value) return;
  if (!WOOD_JOINT_IDS.has(value)) return;
  savePersistedSharedOptions({ woodJoint: value });
}

/**
 * Seed a *new* drawing with sticky colors + glazing + profile + wood joint.
 * Do NOT use this when loading a saved drawing for edit — that must keep its own values.
 */
export function applyPersistedConfiguratorPreferences(defaultConfig, colorFields) {
  return applyPersistedSharedOptions(
    applyPersistedGlazing(applyPersistedFrameColors(defaultConfig, colorFields))
  );
}

/** Track all sticky shared options from a single config change. */
export function trackSharedConfiguratorChange(field, value) {
  trackGlazingChange(field, value);
  trackProfileSystemChange(field, value);
  trackWoodJointChange(field, value);
}
